import {
  YANDEX_METRIKA_AB_TEST_DATA_TOKEN,
  YandexMetrikaAbTestApiResponse,
  YandexMetrikaAbTestContext,
  YandexMetrikaAbTestData,
  YandexMetrikaAbTestFlagKeys,
  YandexMetrikaAbTestFlags,
} from '@analytics/const';
import { AnalyticsFacade } from '@analytics/data-access';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, makeStateKey, OnDestroy, Optional, PLATFORM_ID, TransferState } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CookieKeys, environment, Sentry, SENTRY } from '@core/const';
import { CookieService } from '@core/feature';
import { ExternalScriptLoaderService } from '@core/utils';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { distinctUntilKeyChanged, filter, map, switchMap, tap } from 'rxjs/operators';

declare global {
  interface Window {
    ymab: any;
    ym: ((...args: any[]) => void) & {
      a?: any[];
      l?: number;
    };
  }
}

function isYandexMetrikaAvailable(
  document: Document,
): document is Document & { defaultView: Window & { ym: (...args: any[]) => void } } {
  return !!(document.defaultView && typeof document.defaultView.ym === 'function');
}

@Injectable({
  providedIn: 'root',
})
export class YandexMetrikaService implements OnDestroy {
  private apiKey = environment.yandexMetrikaApiKey;
  private abTestDataTransferStateKey = makeStateKey<YandexMetrikaAbTestData>('yandexMetrikaAbTestData');
  private abTestData$ = new ReplaySubject<YandexMetrikaAbTestData>(1);
  private subscription = new Subscription();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    @Optional() @Inject(YANDEX_METRIKA_AB_TEST_DATA_TOKEN) private abTestDataFromServer: YandexMetrikaAbTestData | null,
    private transferState: TransferState,
    private router: Router,
    private http: HttpClient,
    private cookieService: CookieService,
    @Inject(SENTRY) private sentry: Sentry,
    private analyticsFacade: AnalyticsFacade,
    private externalScriptLoaderService: ExternalScriptLoaderService,
  ) {}

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  initOnServer(): void {
    const abTestData = this.abTestDataFromServer || this.getAbTestDataDefaultValue();

    this.setAbTestData(abTestData);
    this.transferState.set(this.abTestDataTransferStateKey, abTestData);
  }

  initOnClient(): void {
    const abTestDataFromTransferState = this.getAbTestDataFromTransferState();

    this.setAbTestData(abTestDataFromTransferState);

    if (environment.production && this.isEnabled()) {
      this.initYandexMetrikaScript(abTestDataFromTransferState.experiments);
      this.initVarioqubScript();
      if (isYandexMetrikaAvailable(this.document)) {
        this.document.defaultView.ym(this.apiKey, 'getClientID', (clientID: string) => {
          this.setSentryClientId(clientID);
        });
      }
    } else {
      this.initDebugMode();
    }
  }

  getFlags(): Observable<YandexMetrikaAbTestFlags> {
    return this.abTestData$.asObservable().pipe(
      filter((data) => Boolean(data)),
      distinctUntilKeyChanged('experiments'),
      map((data) => data.flags),
    );
  }

  getFlagValue(flag: YandexMetrikaAbTestFlagKeys): Observable<string | undefined> {
    return this.getFlags().pipe(map((flags) => (flags[flag] ? flags[flag][0] : undefined)));
  }

  hasFlag(flag: YandexMetrikaAbTestFlagKeys): Observable<boolean> {
    return this.getFlags().pipe(map((flags) => flag in flags));
  }

  reachGoal(goal: string, params?: Record<any, string>): void {
    if (!this.isEnabled() || !this.isInitialized()) {
      return;
    }

    if (isYandexMetrikaAvailable(this.document)) {
      this.document.defaultView.ym(this.apiKey, 'reachGoal', goal, params);
    }
  }

  setParams(params: Record<string, unknown>): void {
    if (!this.isEnabled() || !this.isInitialized()) {
      return;
    }

    if (isYandexMetrikaAvailable(this.document)) {
      this.document.defaultView.ym(this.apiKey, 'params', params);
    }
  }

  initFirstVisitLog(): void {
    const callback = (): void => {
      const ymUid = this.cookieService.get(CookieKeys.YandexMetrikaUid);
      const visitDate = this.cookieService.get(CookieKeys.YandexMetrikaInitDate);

      // первая реализация работала некорректно, чтобы заново отправлять
      // событие создан новый ключ, этот старый удаляем
      if (CookieKeys.YandexMetrikaLoggedUid) {
        this.cookieService.remove(CookieKeys.YandexMetrikaLoggedUid);
      }

      // если yandex metrika не инициализировалась
      if (!ymUid || !visitDate) {
        return;
      }

      // если уже отправляли лог первого визита
      if (CookieKeys.YandexMetrikaLoggedUidV2 === ymUid) {
        return;
      }

      // яндекс хранит свои куки год, вот и мы сделаем тоже самое
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1);

      this.cookieService.put(CookieKeys.YandexMetrikaLoggedUidV2, ymUid, { path: '/', expires, secure: true });

      this.analyticsFacade.firstVisitLog({
        referer: this.document.referrer,
        visitDate,
        ymUid,
      });
    };

    this.document.addEventListener(`yacounter${this.apiKey}inited`, callback, { once: true });
  }

  private setSentryClientId(yandexClientId: string): void {
    const scope = this.sentry.getCurrentScope();
    scope.setContext('Yandex', { clientId: yandexClientId });
  }

  private isEnabled(): boolean {
    if (this.cookieService.isAutomationTest() || this.cookieService.get(CookieKeys.ATTDisableAnalytics) === '1') {
      return false;
    }

    return true;
  }

  private isInitialized(): boolean {
    return isYandexMetrikaAvailable(this.document);
  }

  private isAbTestsDisabled(): boolean {
    return this.cookieService.isAutomationTest();
  }

  private setAbTestData(data: YandexMetrikaAbTestData): void {
    this.abTestData$.next(data);
  }

  /**
   * Для Yandex Metrika:
   * - втыкает windows.ym, где у метрики очередь ивентов.
   */
  loadYandexMetrikaWithFallback(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return Promise.resolve();

    const view = this.document.defaultView;
    if (!view) return Promise.resolve();

    this.ensureYmStub(view);

    const primarySrc = 'https://mc.yandex.ru/metrika/tag.js';
    //  fallback-ручка в кракене https://gl.sdvor.com/sap-commerce/infrastructure/krakend/-/merge_requests/132
    const fallbackSrc = environment.apiBaseUrlCsr + '/utils/tag.js';

    return this.externalScriptLoaderService.loadWithFallback(primarySrc, fallbackSrc, {
      timeoutMs: 3000,
      insertIntoHead: true,
    });
  }

  private ensureYmStub(view: Window): void {
    // взято из стандартного скприта yandex-метрики https://yandex.ru/adv/edu/materials/metrika-ustanovka-koda-schtchika-na-sayt
    const key = 'ym' as const;

    // очередь событий (таким образом, если звали, например, reachGoal до того, как счтчик проинициализировался - событие не потеряется)
    const existing = view[key];
    if (typeof existing !== 'function') {
      view[key] = function (...args: unknown[]): void {
        const fn = view[key] as any;
        fn.a = fn.a || [];
        fn.a.push(args);
      };
    }

    // Некая  "load start" метка?
    view[key].l = Date.now();
  }

  private initYandexMetrikaScript(experiments?: string): void {
    if (!this.document.defaultView) {
      return;
    }

    this.loadYandexMetrikaWithFallback().then(() => {
      if (isYandexMetrikaAvailable(this.document)) {
        this.document.defaultView.ym(this.apiKey, 'init', {
          clickmap: true,
          trackLinks: true,
          accurateTrackBounce: true,
          webvisor: true,
          ecommerce: 'dataLayer',
          triggerEvent: true,
        });

        // Если распределение уже произошло на ssr, то необходимо передать данные в метрику, воизбежание рассинхрона
        // https://yandex.ru/support/varioqub/api-usersplit.html#response-format__experiments
        if (experiments) {
          this.document.defaultView.ym(this.apiKey, 'experiments', experiments);
        }
      }
    });
  }

  private initVarioqubScript(): void {
    if (this.isAbTestsDisabled()) {
      this.log('Yandex Varioqub отключен');

      return;
    }

    if (!this.document.defaultView) {
      return;
    }

    /* eslint-disable */
    (function (
      e: Window,
      x: Document,
      pe: 'script',
      r: string,
      i: 'ymab',
      me?: HTMLScriptElement,
      nt?: HTMLScriptElement,
    ) {
      ((e[i] =
        e[i] ||
        function () {
          (e[i].a = e[i].a || []).push(arguments);
        }),
        (me = x.createElement(pe)),
        (me.async = true),
        (me.src = r),
        (nt = x.getElementsByTagName(pe)[0]),
        nt.parentNode?.insertBefore(me, nt));
    })(this.document.defaultView, this.document, 'script', 'https://abt.s3.yandex.net/expjs/latest/exp.js', 'ymab');
    /* eslint-enable */

    this.subscription.add(
      this.getClientContext().subscribe(() => {
        if (isYandexMetrikaAvailable(this.document)) {
          this.document.defaultView.ymab(
            `metrika.${this.apiKey}`,
            'init',
            (abTestDataFromClient: YandexMetrikaAbTestData) => {
              this.setAbTestData({ ...abTestDataFromClient, ready: true });
            },
          );
        }
      }),
    );
  }

  private initDebugMode(): void {
    if (this.isAbTestsDisabled()) {
      return;
    }

    this.log('Включен дебаг режим Yandex Varioqub');

    this.subscription.add(
      this.getClientContext()
        .pipe(switchMap((ctx) => this.getAbTestDataFromDebug(ctx)))
        .subscribe((abTestDataFromDebug) => {
          this.setAbTestData(abTestDataFromDebug);
        }),
    );
  }

  private getClientContext(): Observable<YandexMetrikaAbTestContext> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => ({
        url: this.document.location.href,
      })),
    );
  }

  private getAbTestDataFromDebug(ctx: YandexMetrikaAbTestContext): Observable<YandexMetrikaAbTestData> {
    // кука которую проставляет яндекс, если назначил пользователю
    // вариант, в дебаг режиме скрипт вариаокуба не инициализируется,
    // поэтому проставляем ее сами
    const cookieKey = '_ymab_param';
    let params = new HttpParams({
      fromObject: {
        client_id: `metrika.${this.apiKey}`,
        url: ctx.url,
      },
    });

    if (this.cookieService.hasKey(cookieKey)) {
      params = params.set('i', this.cookieService.get(cookieKey));
    }

    return this.http.get<YandexMetrikaAbTestApiResponse>('https://uaas.yandex.ru/v1/exps', { params }).pipe(
      tap((res) => {
        if (!this.cookieService.hasKey(cookieKey)) {
          this.cookieService.put(cookieKey, res.i, { path: '/' });
        }
      }),
      map<YandexMetrikaAbTestApiResponse, YandexMetrikaAbTestData>((res) => ({
        i: res.i,
        experiments: res.experiments,
        flags: res.flags.reduce<Record<string, string[]>>((acc, { n, v, t }) => {
          if (t !== 'flag') {
            return acc;
          }

          const storage = acc[n];

          if (storage) {
            storage.push(v);
          } else {
            acc[n] = [v];
          }

          return acc;
        }, {}),
        ready: true,
      })),
    );
  }

  private getAbTestDataFromTransferState(): YandexMetrikaAbTestData {
    return this.transferState.get(this.abTestDataTransferStateKey, this.getAbTestDataDefaultValue());
  }

  private getAbTestDataDefaultValue(): YandexMetrikaAbTestData {
    return {
      flags: {},
      ready: true,
    };
  }

  private log(message: string): void {
    // eslint-disable-next-line no-console
    console.log(message);
  }
}
